import { Box, Typography } from '@material-ui/core';
import MaterialGrid from '@material-ui/core/Grid';
import axios from 'axios';
import {
  InputLabelOutlined,
  MaterialCheckbox,
  SelectLabel,
} from 'common/components/fields/fields';
import {
  cepMask,
  cpfMask,
  currencyMask,
  dateMask,
  DDDMask,
  phoneWithoutDDDMask,
} from 'helpers/masks';
import { FORM_RULES } from 'helpers/validations';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { change, Field } from 'redux-form';
import {
  propsDeCampoTravavel,
  rotuloKyc,
  TIPOS_EMPRESA,
  UFS,
} from './recebedorFormulario';

/**
 * Campos de KYC do recebedor exigidos pela Pagar.me (Core API v5). Todos
 * recebem `prefixo` (caminho no redux-form) e `travado(caminho)`, que bloqueia
 * o que já foi enviado à Pagar.me: a v5 não permite editar campo preenchido.
 * Campo travado também não é validado (ver `propsDeCampoTravavel`).
 */

// Arrays de validação fora do render: redux-form re-registra o campo quando a
// referência muda.
const OBRIGATORIO = [FORM_RULES.required];
const OBRIGATORIO_SEM_ESPACOS = [FORM_RULES.obrigatorioSemEspacos];
const VALIDA_CEP = [FORM_RULES.required, FORM_RULES.cepSeguro];
const VALIDA_UF = [FORM_RULES.required, FORM_RULES.uf];
const DATA_OBRIGATORIA = [FORM_RULES.required, FORM_RULES.dataBR];
const DATA_OPCIONAL = [FORM_RULES.dataBR];
const VALIDA_CPF = [FORM_RULES.required, FORM_RULES.cpf];
const VALIDA_EMAIL = [FORM_RULES.required, FORM_RULES.email];
const DECLARACAO = [FORM_RULES.mustBeTrue];

const nuncaTravado = () => false;

/** CEP que, ao completar 8 dígitos digitados, preenche o resto pelo ViaCEP. */
function CampoCep(props) {
  const { input, meta, enderecoPrefixo, travado } = props;

  useEffect(() => {
    const cep = String(input.value || '').replace(/\D/g, '');
    if (cep.length !== 8 || !meta.dirty) return undefined;
    let cancelado = false;
    axios
      .get(`https://viacep.com.br/ws/${cep}/json/`)
      .then(({ data }) => {
        if (cancelado || !data || data.erro) return;
        const preencher = (campo, valor) => {
          const caminho = `${enderecoPrefixo}.${campo}`;
          if (valor && !travado(caminho))
            meta.dispatch(change(meta.form, caminho, valor));
        };
        preencher('street', data.logradouro);
        preencher('neighborhood', data.bairro);
        preencher('city', data.localidade);
        preencher('state', data.uf);
      })
      .catch(() => {});
    return () => {
      cancelado = true;
    };
  }, [input.value]);

  return <InputLabelOutlined {...props} />;
}

CampoCep.propTypes = {
  enderecoPrefixo: PropTypes.string,
  input: PropTypes.shape({ value: PropTypes.any }),
  meta: PropTypes.shape({
    dirty: PropTypes.bool,
    dispatch: PropTypes.func,
    form: PropTypes.string,
  }),
  travado: PropTypes.func,
};

export function EnderecoFields({ prefixo, travado = nuncaTravado }) {
  return (
    <MaterialGrid container spacing={3}>
      <MaterialGrid item xs={12} md={4}>
        <Field
          component={CampoCep}
          label="CEP"
          name={`${prefixo}.zipCode`}
          required
          margin="normal"
          {...propsDeCampoTravavel(travado(`${prefixo}.zipCode`), VALIDA_CEP)}
          enderecoPrefixo={prefixo}
          travado={travado}
          {...cepMask}
        />
      </MaterialGrid>
      <MaterialGrid item xs={12} md={8}>
        <Field
          component={InputLabelOutlined}
          label="Rua"
          name={`${prefixo}.street`}
          required
          margin="normal"
          {...propsDeCampoTravavel(
            travado(`${prefixo}.street`),
            OBRIGATORIO_SEM_ESPACOS
          )}
        />
      </MaterialGrid>
      <MaterialGrid item xs={4} md={3}>
        <Field
          component={InputLabelOutlined}
          label="Número"
          name={`${prefixo}.streetNumber`}
          required
          margin="normal"
          {...propsDeCampoTravavel(
            travado(`${prefixo}.streetNumber`),
            OBRIGATORIO_SEM_ESPACOS
          )}
        />
      </MaterialGrid>
      <MaterialGrid item xs={8} md={9}>
        <Field
          component={InputLabelOutlined}
          label="Complemento"
          name={`${prefixo}.complementary`}
          required
          margin="normal"
          helperText="Se não houver, escreva: Sem complemento"
          {...propsDeCampoTravavel(
            travado(`${prefixo}.complementary`),
            OBRIGATORIO_SEM_ESPACOS
          )}
        />
      </MaterialGrid>
      <MaterialGrid item xs={12} md={4}>
        <Field
          component={InputLabelOutlined}
          label="Bairro"
          name={`${prefixo}.neighborhood`}
          required
          margin="normal"
          {...propsDeCampoTravavel(
            travado(`${prefixo}.neighborhood`),
            OBRIGATORIO_SEM_ESPACOS
          )}
        />
      </MaterialGrid>
      <MaterialGrid item xs={8} md={5}>
        <Field
          component={InputLabelOutlined}
          label="Cidade"
          name={`${prefixo}.city`}
          required
          margin="normal"
          {...propsDeCampoTravavel(
            travado(`${prefixo}.city`),
            OBRIGATORIO_SEM_ESPACOS
          )}
        />
      </MaterialGrid>
      <MaterialGrid item xs={4} md={3}>
        <Field
          component={SelectLabel}
          label="UF"
          name={`${prefixo}.state`}
          options={UFS}
          required
          margin="normal"
          {...propsDeCampoTravavel(travado(`${prefixo}.state`), VALIDA_UF)}
        />
      </MaterialGrid>
      <MaterialGrid item xs={12}>
        <Field
          component={InputLabelOutlined}
          label="Ponto de referência"
          name={`${prefixo}.referencePoint`}
          required
          margin="normal"
          {...propsDeCampoTravavel(
            travado(`${prefixo}.referencePoint`),
            OBRIGATORIO_SEM_ESPACOS
          )}
        />
      </MaterialGrid>
    </MaterialGrid>
  );
}

EnderecoFields.propTypes = {
  prefixo: PropTypes.string.isRequired,
  travado: PropTypes.func,
};

/** Titular pessoa física. `prefixo` é o objeto do formulário (ex.: create_account). */
export function DadosPessoaFisicaFields({
  prefixo,
  travado = nuncaTravado,
  cpfTravado = false,
}) {
  return (
    <MaterialGrid item xs={12}>
      <Typography variant="subtitle1">Dados do titular da conta</Typography>
      <MaterialGrid container spacing={3}>
        <MaterialGrid item xs={12} md={6}>
          <Field
            component={InputLabelOutlined}
            label="Nome completo"
            name={`${prefixo}.bankAccount.ownerName`}
            required
            margin="normal"
            {...propsDeCampoTravavel(
              travado(`${prefixo}.bankAccount.ownerName`),
              OBRIGATORIO_SEM_ESPACOS
            )}
          />
        </MaterialGrid>
        <MaterialGrid item xs={12} md={6}>
          <Field
            component={InputLabelOutlined}
            label="CPF"
            name={`${prefixo}.bankAccount.cpf`}
            required
            margin="normal"
            {...propsDeCampoTravavel(
              cpfTravado || travado(`${prefixo}.bankAccount.cpf`),
              VALIDA_CPF
            )}
            {...cpfMask}
          />
        </MaterialGrid>
        <MaterialGrid item xs={12} md={6}>
          <Field
            component={InputLabelOutlined}
            label="Nome da mãe (opcional)"
            name={`${prefixo}.individual.motherName`}
            margin="normal"
            {...propsDeCampoTravavel(
              travado(`${prefixo}.individual.motherName`)
            )}
          />
        </MaterialGrid>
        <MaterialGrid item xs={12} md={6}>
          <Field
            component={InputLabelOutlined}
            label="Data de nascimento"
            name={`${prefixo}.individual.birthdate`}
            required
            margin="normal"
            {...propsDeCampoTravavel(
              travado(`${prefixo}.individual.birthdate`),
              DATA_OBRIGATORIA
            )}
            {...dateMask}
          />
        </MaterialGrid>
        <MaterialGrid item xs={12} md={6}>
          <Field
            component={InputLabelOutlined}
            label="Renda mensal"
            name={`${prefixo}.individual.monthlyIncome`}
            required
            margin="normal"
            {...propsDeCampoTravavel(
              travado(`${prefixo}.individual.monthlyIncome`),
              OBRIGATORIO
            )}
            {...currencyMask}
          />
        </MaterialGrid>
        <MaterialGrid item xs={12} md={6}>
          <Field
            component={InputLabelOutlined}
            label="Profissão"
            name={`${prefixo}.individual.professionalOccupation`}
            required
            margin="normal"
            {...propsDeCampoTravavel(
              travado(`${prefixo}.individual.professionalOccupation`),
              OBRIGATORIO_SEM_ESPACOS
            )}
          />
        </MaterialGrid>
      </MaterialGrid>
      <Typography variant="body2" style={{ paddingTop: 12 }}>
        Endereço do titular
      </Typography>
      <EnderecoFields
        prefixo={`${prefixo}.individual.address`}
        travado={travado}
      />
    </MaterialGrid>
  );
}

DadosPessoaFisicaFields.propTypes = {
  cpfTravado: PropTypes.bool,
  prefixo: PropTypes.string.isRequired,
  travado: PropTypes.func,
};

/** Empresa: nome, razão social e CNPJ vêm do cadastro do estabelecimento. */
export function DadosEmpresaFields({
  prefixo,
  travado = nuncaTravado,
  establishment,
}) {
  return (
    <MaterialGrid item xs={12}>
      <Typography variant="subtitle1">Dados da empresa</Typography>
      <Typography variant="body2">
        Nome fantasia: {establishment?.name || '-'}
      </Typography>
      <Typography variant="body2">
        Razão social: {establishment?.social_name || '-'}
      </Typography>
      <Typography variant="body2">
        CNPJ: {establishment?.cnpj || '-'}
      </Typography>
      <MaterialGrid container spacing={3}>
        <MaterialGrid item xs={12} md={4}>
          <Field
            component={InputLabelOutlined}
            label="Faturamento anual"
            name={`${prefixo}.corporation.annualRevenue`}
            required
            margin="normal"
            {...propsDeCampoTravavel(
              travado(`${prefixo}.corporation.annualRevenue`),
              OBRIGATORIO
            )}
            {...currencyMask}
          />
        </MaterialGrid>
        <MaterialGrid item xs={12} md={4}>
          <Field
            component={SelectLabel}
            label="Tipo de empresa (opcional)"
            name={`${prefixo}.corporation.corporationType`}
            options={TIPOS_EMPRESA}
            margin="normal"
            {...propsDeCampoTravavel(
              travado(`${prefixo}.corporation.corporationType`)
            )}
          />
        </MaterialGrid>
        <MaterialGrid item xs={12} md={4}>
          <Field
            component={InputLabelOutlined}
            label="Data de fundação (opcional)"
            name={`${prefixo}.corporation.foundingDate`}
            margin="normal"
            {...propsDeCampoTravavel(
              travado(`${prefixo}.corporation.foundingDate`),
              DATA_OPCIONAL
            )}
            {...dateMask}
          />
        </MaterialGrid>
      </MaterialGrid>
      <Typography variant="body2" style={{ paddingTop: 12 }}>
        Endereço da empresa
      </Typography>
      <EnderecoFields
        prefixo={`${prefixo}.corporation.mainAddress`}
        travado={travado}
      />
    </MaterialGrid>
  );
}

DadosEmpresaFields.propTypes = {
  establishment: PropTypes.shape({
    cnpj: PropTypes.any,
    name: PropTypes.any,
    social_name: PropTypes.any,
  }),
  prefixo: PropTypes.string.isRequired,
  travado: PropTypes.func,
};

/**
 * Um único representante legal: a Pagar.me aceita só um sócio do QSA por
 * empresa, e o formulário antigo permitia vários.
 */
export function RepresentanteLegalFields({
  prefixo = 'managingPartners',
  travado = nuncaTravado,
}) {
  const base = `${prefixo}[0]`;
  return (
    <MaterialGrid item xs={12}>
      <Box
        style={{
          padding: 16,
          marginTop: 12,
          backgroundColor: '#f7f7f7',
          borderRadius: 4,
        }}
      >
        <Typography variant="subtitle1">Representante legal</Typography>
        <Typography variant="body2">
          Representante legal: sócio que consta no QSA do CNPJ (não são aceitos
          administradores nem procuradores)
        </Typography>
        <MaterialGrid container spacing={3}>
          <MaterialGrid item xs={12} md={6}>
            <Field
              component={InputLabelOutlined}
              label="Nome completo"
              name={`${base}.name`}
              required
              margin="normal"
              {...propsDeCampoTravavel(
                travado(`${base}.name`),
                OBRIGATORIO_SEM_ESPACOS
              )}
            />
          </MaterialGrid>
          <MaterialGrid item xs={12} md={6}>
            <Field
              component={InputLabelOutlined}
              label="CPF"
              name={`${base}.documentNumber`}
              required
              margin="normal"
              {...propsDeCampoTravavel(
                travado(`${base}.documentNumber`),
                VALIDA_CPF
              )}
              {...cpfMask}
            />
          </MaterialGrid>
          <MaterialGrid item xs={12} md={6}>
            <Field
              component={InputLabelOutlined}
              label="E-mail"
              name={`${base}.email`}
              required
              margin="normal"
              {...propsDeCampoTravavel(travado(`${base}.email`), VALIDA_EMAIL)}
            />
          </MaterialGrid>
          <MaterialGrid item xs={12} md={6}>
            <Field
              component={InputLabelOutlined}
              label="Nome da mãe (opcional)"
              name={`${base}.motherName`}
              margin="normal"
              {...propsDeCampoTravavel(travado(`${base}.motherName`))}
            />
          </MaterialGrid>
          <MaterialGrid item xs={12} md={4}>
            <Field
              component={InputLabelOutlined}
              label="Data de nascimento"
              name={`${base}.birthdate`}
              required
              margin="normal"
              {...propsDeCampoTravavel(
                travado(`${base}.birthdate`),
                DATA_OBRIGATORIA
              )}
              {...dateMask}
            />
          </MaterialGrid>
          <MaterialGrid item xs={12} md={4}>
            <Field
              component={InputLabelOutlined}
              label="Renda mensal"
              name={`${base}.monthlyIncome`}
              required
              margin="normal"
              {...propsDeCampoTravavel(
                travado(`${base}.monthlyIncome`),
                OBRIGATORIO
              )}
              {...currencyMask}
            />
          </MaterialGrid>
          <MaterialGrid item xs={12} md={4}>
            <Field
              component={InputLabelOutlined}
              label="Profissão"
              name={`${base}.professionalOccupation`}
              required
              margin="normal"
              {...propsDeCampoTravavel(
                travado(`${base}.professionalOccupation`),
                OBRIGATORIO_SEM_ESPACOS
              )}
            />
          </MaterialGrid>
          <MaterialGrid item xs={4} md={3}>
            <Field
              component={InputLabelOutlined}
              label="DDD"
              name={`${base}.phone.ddd`}
              required
              margin="normal"
              {...propsDeCampoTravavel(
                travado(`${base}.phone.ddd`),
                OBRIGATORIO
              )}
              {...DDDMask}
            />
          </MaterialGrid>
          <MaterialGrid item xs={8} md={3}>
            <Field
              component={InputLabelOutlined}
              label="Telefone"
              name={`${base}.phone.number`}
              required
              margin="normal"
              {...propsDeCampoTravavel(
                travado(`${base}.phone.number`),
                OBRIGATORIO
              )}
              {...phoneWithoutDDDMask}
            />
          </MaterialGrid>
        </MaterialGrid>
        <Typography variant="body2" style={{ paddingTop: 12 }}>
          Endereço do representante
        </Typography>
        <EnderecoFields prefixo={`${base}.address`} travado={travado} />
        <Field
          component={MaterialCheckbox}
          label="Declaro que sou representante legal desta empresa"
          name={`${base}.selfDeclaredLegalRepresentative`}
          {...propsDeCampoTravavel(
            travado(`${base}.selfDeclaredLegalRepresentative`),
            DECLARACAO
          )}
        />
      </Box>
    </MaterialGrid>
  );
}

RepresentanteLegalFields.propTypes = {
  prefixo: PropTypes.string,
  travado: PropTypes.func,
};

/** Situação do recebedor na Pagar.me e aviso sobre campos travados. */
export function SituacaoRecebedor({ recebedor }) {
  if (!recebedor || (!recebedor.id && !recebedor.status)) return null;
  return (
    <Box
      style={{
        padding: 12,
        marginBottom: 16,
        backgroundColor: '#f5f5f5',
        borderRadius: 4,
      }}
    >
      <Typography variant="body2">
        Situação na Pagar.me:{' '}
        {recebedor.statusLabel || recebedor.status || 'Não informada'}
      </Typography>
      <Typography variant="body2">
        Análise cadastral: {rotuloKyc(recebedor.kycStatus)}
      </Typography>
      {recebedor.status && recebedor.status !== 'active' ? (
        <Typography variant="caption" display="block">
          O saque pode ser recusado pela Pagar.me enquanto a conta não estiver
          ativa.
        </Typography>
      ) : null}
      {recebedor.registroTravado ? (
        <Typography variant="caption" display="block">
          Dados já enviados à Pagar.me não podem ser alterados por aqui; para
          corrigir, fale com o suporte.
        </Typography>
      ) : null}
    </Box>
  );
}

SituacaoRecebedor.propTypes = {
  recebedor: PropTypes.shape({
    id: PropTypes.any,
    kycStatus: PropTypes.any,
    registroTravado: PropTypes.bool,
    status: PropTypes.any,
    statusLabel: PropTypes.any,
  }),
};
